import {
  ResponderMapProvider,
  useResponderMap,
  ZoneBuilder,
  ZoneLinksPanel,
  ZoneMapEdit,
  ZoneTreePanel,
} from 'features/Responder';
import ScheduleNudgePrompt from 'features/Responder/components/ScheduleNudgePrompt';
import { PersonnelProvider } from 'features/Responder/features/Personnel';
import {
  SchedulesProvider,
  useSchedules,
} from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import { RunHistory } from 'features/Responder/features/Schedules/features/Runs/features/RunHistory';
import {
  isRunOpen,
  resolveRunScheduleId,
  resolveScheduleZoneIds,
} from 'features/Responder/features/Schedules/utils';
import { TourProvider, useTours } from 'features/Responder/features/Tours';
import { useZones, ZonesProvider } from 'features/Responder/features/Zones';
import {
  estimateChildSeedSizeMeters,
  normalizePolygon,
} from 'features/Responder/features/Zones';
import { fitMapToZone } from 'features/Responder/features/Zones';
import 'pages/Responder/zones.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Col, Container, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useMap } from 'react-leaflet';
import { Group, Panel } from 'react-resizable-panels';
import { useNavigate } from 'react-router-dom';
import { ResizablePanelHandle } from 'utils/panelResizeUtils';

const ZoneExplorerTabButton = ({ active, onClick, children }) => (
  <Button
    size="sm"
    variant="outline-light"
    onClick={onClick}
    className={`px-3 zone-explorer-tab-btn ${active ? 'zone-explorer-tab-btn--active' : ''}`}
  >
    {children}
  </Button>
);

const ZoneMapFocusLayer = ({ zone, zones, focusKey = null }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !zone || focusKey == null) return;
    fitMapToZone(map, zone, zones);
    // Intentionally key fit behavior to explicit focus events only.
    // Dragging/resizing updates zone geometry frequently and should not auto-refit the map.
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, map]);

  return null;
};

const ZoneListPanel = ({
  zones = [],
  activeZoneId = null,
  zoneExplorerMode = 'list',
  onSetZoneExplorerMode = () => {},
  onSelectZone,
  onCreateZone = () => {},
  createDisabled = false,
  onEditZone = () => {},
  onDeleteZone = () => {},
  showDeleteAction = true,
  showAllZonesOnMap = false,
  onToggleShowAllZones = () => {},
}) => {
  const [search, setSearch] = useState('');

  const filteredZones = useMemo(() => {
    if (!search.trim()) return zones;
    const query = search.trim().toLowerCase();
    return zones.filter((zone) => (zone?.name || '').toLowerCase().includes(query));
  }, [search, zones]);

  return (
    <div className="card border-0 shadow-lg text-white h-100">
      <div className="card-body d-flex flex-column p-2" style={{ minHeight: 0 }}>
        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
          <div className="d-flex flex-column gap-1">
            <div className="zone-panel-heading-copy">
              <h6 className="mb-0">Zone List</h6>
              <div className="text-muted small">All zones</div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <ZoneExplorerTabButton
                active={zoneExplorerMode === 'list'}
                onClick={() => onSetZoneExplorerMode('list')}
              >
                Zone List
              </ZoneExplorerTabButton>
              <ZoneExplorerTabButton
                active={zoneExplorerMode === 'tree'}
                onClick={() => onSetZoneExplorerMode('tree')}
              >
                Zone Tree
              </ZoneExplorerTabButton>
              <ZoneExplorerTabButton
                active={zoneExplorerMode === 'links'}
                onClick={() => onSetZoneExplorerMode('links')}
              >
                Zone Links
              </ZoneExplorerTabButton>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button size="sm" variant="primary" onClick={onCreateZone} disabled={createDisabled}>
              Create
            </Button>
            <Form.Check
              type="switch"
              id="toggle-show-all-zones"
              label="Show all"
              checked={showAllZonesOnMap}
              onChange={(event) => onToggleShowAllZones(event.target.checked)}
              className="zone-list-show-all-toggle mb-0"
            />
            <Badge bg="secondary">{filteredZones.length}</Badge>
          </div>
        </div>

        <div className="zone-list-search-wrap">
          <Form.Control
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search zones..."
            size="sm"
            className="mb-0"
          />
        </div>

        <div
          className="zone-list-items flex-grow-1 overflow-auto d-flex flex-column gap-1"
          style={{ minHeight: 0 }}
        >
          {filteredZones.length ? (
            filteredZones.map((zone) => {
              const isActive = activeZoneId != null && `${activeZoneId}` === `${zone?.id}`;
              return (
                <div
                  key={zone?.id ?? zone?.name}
                  className={`zone-selection-row responder-interactive-item ${
                    isActive ? 'is-selected' : ''
                  }`}
                >
                  <button
                    type="button"
                    className="zone-selection-item zone-selection-item-btn"
                    onClick={() => onSelectZone?.(zone)}
                  >
                    {zone?.name || `Zone #${zone?.id}`}
                  </button>
                  <div className="zone-selection-actions d-flex align-items-center gap-1">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip className="responder-tooltip" id={`zone-list-edit-${zone?.id}`}>
                          Edit zone
                        </Tooltip>
                      }
                    >
                      <button
                        type="button"
                        className="zone-inline-action responder-interactive-action"
                        aria-label="Edit zone"
                        onClick={() => onEditZone?.(zone)}
                      >
                        <FaEdit size={11} />
                      </button>
                    </OverlayTrigger>
                    {showDeleteAction && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip
                            className="responder-tooltip"
                            id={`zone-list-delete-${zone?.id}`}
                          >
                            Delete zone
                          </Tooltip>
                        }
                      >
                        <button
                          type="button"
                          className="zone-inline-action responder-interactive-action"
                          aria-label="Delete zone"
                          onClick={() => onDeleteZone?.(zone)}
                        >
                          <FaTrash size={11} />
                        </button>
                      </OverlayTrigger>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-muted small">No zones match your search.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const ZoneTelemetryPanel = ({
  activeZone,
  scopeZoneIds = [],
  linkedSchedulesCount = 0,
  totalRuns = 0,
  activeRunsCount = 0,
}) => {
  const zoneLabel =
    activeZone?.name || (activeZone?.id != null ? `Zone #${activeZone.id}` : 'None');

  return (
    <div className="card border-0 shadow-lg text-white h-100">
      <div className="card-body d-flex flex-column p-2" style={{ minHeight: 0 }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h6 className="mb-0">Zone Telemetry</h6>
          </div>
          <Badge bg={activeZone ? 'primary' : 'secondary'}>
            {activeZone ? 'Selected' : 'None'}
          </Badge>
        </div>

        <div className="small d-flex flex-column gap-2">
          <div>
            <strong>Zone:</strong> {zoneLabel}
          </div>
          <div>
            <strong>Scope:</strong> {scopeZoneIds.length} zone{scopeZoneIds.length === 1 ? '' : 's'}
          </div>
          <div>
            <strong>Linked schedules:</strong> {linkedSchedulesCount}
          </div>
          <div>
            <strong>Total runs:</strong> {totalRuns}
          </div>
          <div>
            <strong>Active runs:</strong> {activeRunsCount}
          </div>
          <hr className="my-1" />
          <div className="text-muted fw-semibold">Telemetry (Placeholder)</div>
          <div>
            <strong>Current occupants:</strong> N/A
          </div>
          <div>
            <strong>Avg time in zone:</strong> N/A
          </div>
          <div>
            <strong>Notifications sent (Last 24h):</strong> N/A
          </div>
          <div>
            <strong>Group:</strong> N/A
          </div>
        </div>
      </div>
    </div>
  );
};

const ZoneActivityPanel = ({
  scheduleIds = [],
  showZoneManager = true,
  onZonePersistSuccess = () => {},
}) => {
  return (
    <div className="card border-0 shadow-lg text-white h-100">
      <div className="card-body d-flex flex-column p-2 gap-2" style={{ minHeight: 0 }}>
        <div
          className="bg-dark border border-secondary rounded p-2 d-flex flex-column"
          style={{ minHeight: 0, flex: showZoneManager ? undefined : '1 1 auto' }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span>Run Event History</span>
            <Badge bg="secondary">{scheduleIds.length}</Badge>
          </div>
          <div
            style={
              showZoneManager
                ? { maxHeight: '22vh', overflow: 'auto' }
                : { flex: 1, minHeight: 0, overflow: 'auto' }
            }
          >
            {scheduleIds.length ? (
              <RunHistory scheduleIds={scheduleIds} showScheduleColumn enableRunSelection />
            ) : (
              <div className="text-muted small">
                No schedules linked to the selected zone scope.
              </div>
            )}
          </div>
        </div>

        {showZoneManager && (
          <div
            className="bg-dark border border-secondary rounded p-2 d-flex flex-column"
            style={{ minHeight: 0 }}
          >
            <div className="mb-2">Zone Manager</div>
            <div style={{ maxHeight: '22vh', overflow: 'auto' }}>
              <ZoneBuilder onPersistSuccess={onZonePersistSuccess} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ZonesContent = () => {
  const navigate = useNavigate();
  const {
    zones,
    zonesWithMeta,
    zonesWithTours,
    activeZoneId,
    setActiveZoneId,
    setSelectedZoneIds,
    deleteZone,
    zonesEditing,
    setZonesEditing,
  } = useZones();
  const {
    selectedZoneIds,
    setSelectedZoneIds: setMapSelectedZoneIds,
    showAllZones,
    setShowAllZones,
    setShowZoneTools,
  } = useResponderMap();
  const { setMapTool, mapTool } = useTours();
  const { schedules, runs } = useSchedules();

  const pageRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(null);
  const [focusNonce, setFocusNonce] = useState(0);
  const [zoneExplorerMode, setZoneExplorerMode] = useState('list');
  const [scheduleNudge, setScheduleNudge] = useState(null);
  const showAllZonesOnMap = !!showAllZones;

  const zonesList = useMemo(
    () =>
      (Array.isArray(zonesWithMeta) && zonesWithMeta.length ? zonesWithMeta : zones || [])
        .slice()
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [zones, zonesWithMeta],
  );
  const hasUnsavedZoneChanges = useMemo(
    () => Array.isArray(zones) && zones.some((zone) => zone?.isModified),
    [zones],
  );

  const zoneById = useMemo(
    () => new Map(zonesList.map((zone) => [`${zone?.id}`, zone])),
    [zonesList],
  );
  const zoneLinksList = useMemo(
    () =>
      (Array.isArray(zonesWithTours) && zonesWithTours.length ? zonesWithTours : zonesList)
        .slice()
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [zonesList, zonesWithTours],
  );
  const pendingParentId = mapTool?.mode === 'zone' ? mapTool?.parentId : null;
  const pendingParentZone = pendingParentId != null ? zoneById.get(`${pendingParentId}`) : null;
  const awaitingZoneShape =
    mapTool?.mode === 'zone' &&
    (!!mapTool?.awaitingShape || (!!pendingParentZone && !mapTool?.shape));
  const zoneChildrenByParent = useMemo(() => {
    const map = new Map();
    zonesList.forEach((zone) => {
      const parentKey = zone?.parentId == null ? null : `${zone.parentId}`;
      if (!parentKey) return;
      const next = map.get(parentKey) || [];
      next.push(`${zone.id}`);
      map.set(parentKey, next);
    });
    return map;
  }, [zonesList]);

  const activeZone = useMemo(() => {
    if (activeZoneId != null) {
      const match = zoneById.get(`${activeZoneId}`);
      if (match) return match;
    }
    const firstSelected =
      Array.isArray(selectedZoneIds) && selectedZoneIds.length ? selectedZoneIds[0] : null;
    if (firstSelected != null) {
      const selectedMatch = zoneById.get(`${firstSelected}`);
      if (selectedMatch) return selectedMatch;
    }
    return zonesList[0] || null;
  }, [activeZoneId, selectedZoneIds, zoneById, zonesList]);

  const resolveScopedZoneIds = useCallback(
    (zone) => {
      if (!zone?.id) return [];
      const firstLayerChildren = (Array.isArray(zone.childrenIds) ? zone.childrenIds : [])
        .map((id) => zoneById.get(`${id}`))
        .filter(Boolean)
        .map((child) => child.id);
      return Array.from(new Set([zone.id, ...firstLayerChildren].map((id) => `${id}`)));
    },
    [zoneById],
  );

  useEffect(() => {
    if (!zonesList.length) return;
    if (Array.isArray(selectedZoneIds) && selectedZoneIds.length > 0) return;
    const initialZone =
      (activeZoneId != null ? zoneById.get(`${activeZoneId}`) : null) || zonesList[0] || null;
    if (!initialZone?.id) return;
    const scopedIds = resolveScopedZoneIds(initialZone);
    if (!scopedIds.length) return;
    setMapSelectedZoneIds(scopedIds);
    setSelectedZoneIds(scopedIds);
    if (activeZoneId == null) setActiveZoneId(initialZone.id);
  }, [
    activeZoneId,
    resolveScopedZoneIds,
    selectedZoneIds,
    setActiveZoneId,
    setMapSelectedZoneIds,
    setSelectedZoneIds,
    zoneById,
    zonesList,
  ]);

  const handleSelectZone = useCallback(
    (zone) => {
      if (!zone?.id) return;
      const canonicalZone = zoneById.get(`${zone.id}`) || zone;
      const scopedZoneIds = resolveScopedZoneIds(canonicalZone);

      setActiveZoneId(canonicalZone.id);
      setMapSelectedZoneIds(scopedZoneIds);
      setSelectedZoneIds(scopedZoneIds);
      setFocusNonce((prev) => prev + 1);
    },
    [resolveScopedZoneIds, setActiveZoneId, setMapSelectedZoneIds, setSelectedZoneIds, zoneById],
  );

  const handleEditZone = useCallback(
    (zone) => {
      if (!zone?.id) return;
      handleSelectZone(zone);
      setZonesEditing(true);
    },
    [handleSelectZone, setZonesEditing],
  );

  const handleCreateZone = useCallback(() => {
    if (awaitingZoneShape) return;
    const parentZone =
      (activeZoneId != null ? zoneById.get(`${activeZoneId}`) : null) || zonesList?.[0] || null;
    const parentPoints = normalizePolygon(
      Array.isArray(parentZone?._points) ? parentZone._points : parentZone?.polygon || [],
    );
    const parentSizeMeters = estimateChildSeedSizeMeters(parentPoints, 0.5, 60);
    const parentLabel = parentZone?.name || `Zone #${parentZone?.id ?? '?'}`;

    setMapTool?.({
      mode: 'zone',
      parentId: parentZone?.id ?? null,
      parentName: parentLabel,
      sizeMeters: parentSizeMeters,
      awaitingShape: true,
    });
    setShowZoneTools?.(true);
  }, [activeZoneId, awaitingZoneShape, setMapTool, setShowZoneTools, zoneById, zonesList]);

  const resolveDescendantZoneKeys = useCallback(
    (zoneId) => {
      if (zoneId == null) return [];
      const descendants = [];
      const stack = [`${zoneId}`];
      while (stack.length) {
        const current = stack.pop();
        const children = zoneChildrenByParent.get(current) || [];
        children.forEach((childKey) => {
          descendants.push(childKey);
          stack.push(childKey);
        });
      }
      return descendants;
    },
    [zoneChildrenByParent],
  );

  const handleDeleteZone = useCallback(
    async (zone) => {
      if (!zone?.id) return;
      const descendantKeys = resolveDescendantZoneKeys(zone.id);
      const childCount = descendantKeys.length;
      const confirmMessage =
        childCount > 0
          ? `Delete this parent zone? This will also delete ${childCount} child zone${
              childCount === 1 ? '' : 's'
            }. This cannot be undone.`
          : 'Delete this zone? This cannot be undone.';
      if (!window.confirm(confirmMessage)) return;
      const removedKeys = new Set([`${zone.id}`, ...descendantKeys]);
      try {
        await deleteZone(zone.id);
        setMapSelectedZoneIds((prev = []) =>
          (Array.isArray(prev) ? prev : []).filter((id) => !removedKeys.has(`${id}`)),
        );
      } catch (error) {
        console.error('Failed to delete zone:', error);
        window.alert('Unable to delete zone right now. Please try again.');
      }
    },
    [deleteZone, resolveDescendantZoneKeys, setMapSelectedZoneIds],
  );

  const handleZonePersistSuccess = useCallback((persistResult) => {
    const createdData = persistResult?.createdResponse?.data;
    const createdZone = Array.isArray(createdData)
      ? createdData[0]
      : createdData && typeof createdData === 'object'
        ? createdData
        : null;
    if (!createdZone || createdZone.id == null) return;
    setScheduleNudge({
      entityType: 'zone',
      entityId: createdZone.id,
      entityName: createdZone.name || '',
    });
  }, []);

  const handleNudgeConfirm = useCallback(() => {
    if (scheduleNudge?.entityId == null) return;
    navigate('/responder/schedules', {
      state: {
        responderSchedulePrefill: {
          entityType: 'zone',
          entityId: scheduleNudge.entityId,
          entityName: scheduleNudge.entityName || '',
          requestedAt: Date.now(),
          source: 'zone-create',
        },
      },
    });
    setScheduleNudge(null);
  }, [navigate, scheduleNudge]);

  const handleNudgeDismiss = useCallback(() => {
    setScheduleNudge(null);
  }, []);

  const zoneScopeIds = useMemo(() => {
    if (Array.isArray(selectedZoneIds) && selectedZoneIds.length) {
      return Array.from(new Set(selectedZoneIds.map((value) => `${value}`)));
    }
    if (activeZone?.id != null) return [`${activeZone.id}`];
    return [];
  }, [activeZone?.id, selectedZoneIds]);

  const visibleZoneIds = useMemo(() => {
    if (showAllZonesOnMap) {
      return zonesList
        .map((zone) => zone?.id)
        .filter((id) => id != null)
        .map((id) => `${id}`);
    }
    return zoneScopeIds;
  }, [showAllZonesOnMap, zoneScopeIds, zonesList]);

  const zoneScheduleIds = useMemo(() => {
    if (!zoneScopeIds.length || !Array.isArray(schedules)) return [];
    const zoneLookup = new Set(zoneScopeIds);
    return Array.from(
      new Set(
        schedules
          .filter((schedule) => {
            const scheduleZoneIds = resolveScheduleZoneIds(schedule);
            return scheduleZoneIds.some((zoneId) => zoneLookup.has(`${zoneId}`));
          })
          .map((schedule) => schedule?.id)
          .filter((id) => id != null)
          .map((id) => `${id}`),
      ),
    );
  }, [schedules, zoneScopeIds]);

  const scopedRuns = useMemo(() => {
    if (!zoneScheduleIds.length || !Array.isArray(runs)) return [];
    const scheduleLookup = new Set(zoneScheduleIds);
    return runs.filter((run) => {
      const scheduleId = resolveRunScheduleId(run);
      return scheduleId != null && scheduleLookup.has(`${scheduleId}`);
    });
  }, [runs, zoneScheduleIds]);

  const activeRunsCount = useMemo(
    () => scopedRuns.filter((run) => isRunOpen(run)).length,
    [scopedRuns],
  );

  const mapOverlays = useMemo(
    () =>
      activeZone ? (
        <ZoneMapFocusLayer
          zone={activeZone}
          zones={zonesWithMeta}
          focusKey={focusNonce > 0 ? `${focusNonce}` : null}
        />
      ) : null,
    [activeZone, focusNonce, zonesWithMeta],
  );

  useEffect(() => {
    setZonesEditing(false);
  }, [setZonesEditing]);

  useEffect(() => {
    if (!hasUnsavedZoneChanges) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      // Browser controls the final message text for beforeunload prompts.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedZoneChanges]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateHeight = () => {
      if (!pageRef.current) return;
      const topOffset = pageRef.current.getBoundingClientRect().top || 0;
      const nextHeight = Math.max(window.innerHeight - topOffset, 320);
      setPageHeight(nextHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return (
    <div
      ref={pageRef}
      className="tour-builder-page zones-page py-4"
      style={{
        minHeight: 0,
        height: pageHeight ? `${pageHeight}px` : '100vh',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Container fluid className="d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
        <Row className="flex-grow-1" style={{ minHeight: 0 }}>
          <Col className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
            <Group
              orientation="horizontal"
              className="d-flex w-100 flex-grow-1 h-100"
              style={{ minHeight: 0, height: '100%' }}
            >
              <Panel
                defaultSize={70}
                minSize={40}
                className="d-flex flex-column h-100"
                style={{ minHeight: 0 }}
              >
                <Group
                  orientation="vertical"
                  className="d-flex flex-column h-100"
                  style={{ minHeight: 0 }}
                >
                  <Panel
                    defaultSize={65}
                    minSize={35}
                    className="d-flex flex-column h-100"
                    style={{ minHeight: 0 }}
                  >
                    <div className="card border-0 shadow-lg bg-dark text-white h-100">
                      <div className="card-body rounded overflow-hidden p-0 h-100">
                        <div className="position-relative w-100 h-100">
                          <div className="w-100 h-100 rounded-top overflow-hidden">
                            <ZoneMapEdit
                              extraLayers={mapOverlays}
                              visibleZoneIds={visibleZoneIds}
                              autoSelectZones={false}
                              enableZoneLayerFocusWhenEditing={showAllZonesOnMap}
                              showZoneEditorPanel={zonesEditing}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Panel>

                  <ResizablePanelHandle direction="horizontal" />

                  <Panel
                    defaultSize={35}
                    minSize={25}
                    className="d-flex flex-column h-100"
                    style={{ minHeight: 0 }}
                  >
                    {zonesEditing ? (
                      <Group
                        orientation="horizontal"
                        className="d-flex w-100 h-100"
                        style={{ minHeight: 0 }}
                      >
                        <Panel
                          defaultSize={35}
                          minSize={25}
                          className="d-flex flex-column h-100"
                          style={{ minHeight: 0 }}
                        >
                          <ZoneTelemetryPanel
                            activeZone={activeZone}
                            scopeZoneIds={zoneScopeIds}
                            linkedSchedulesCount={zoneScheduleIds.length}
                            totalRuns={scopedRuns.length}
                            activeRunsCount={activeRunsCount}
                          />
                        </Panel>
                        <ResizablePanelHandle direction="vertical" />
                        <Panel
                          defaultSize={65}
                          minSize={35}
                          className="d-flex flex-column h-100"
                          style={{ minHeight: 0 }}
                        >
                          <ZoneTreePanel
                            maxHeight="100%"
                            onEditZone={handleEditZone}
                            onDeleteZone={handleDeleteZone}
                            showDeleteAction
                          />
                        </Panel>
                      </Group>
                    ) : (
                      <div className="d-flex flex-column h-100 gap-2" style={{ minHeight: 0 }}>
                        <div className="flex-grow-1" style={{ minHeight: 0 }}>
                          {zoneExplorerMode === 'tree' ? (
                            <ZoneTreePanel
                              maxHeight="100%"
                              zoneExplorerMode={zoneExplorerMode}
                              onSetZoneExplorerMode={setZoneExplorerMode}
                              onEditZone={handleEditZone}
                              onDeleteZone={handleDeleteZone}
                              showDeleteAction={false}
                            />
                          ) : zoneExplorerMode === 'links' ? (
                            <ZoneLinksPanel
                              zones={zoneLinksList}
                              activeZoneId={activeZone?.id ?? null}
                              zoneExplorerMode={zoneExplorerMode}
                              onSetZoneExplorerMode={setZoneExplorerMode}
                              onSelectZone={handleSelectZone}
                            />
                          ) : (
                            <ZoneListPanel
                              zones={zonesList}
                              activeZoneId={activeZone?.id ?? null}
                              zoneExplorerMode={zoneExplorerMode}
                              onSetZoneExplorerMode={setZoneExplorerMode}
                              onSelectZone={handleSelectZone}
                              onCreateZone={handleCreateZone}
                              createDisabled={awaitingZoneShape}
                              onEditZone={handleEditZone}
                              onDeleteZone={handleDeleteZone}
                              showDeleteAction={false}
                              showAllZonesOnMap={showAllZonesOnMap}
                              onToggleShowAllZones={setShowAllZones}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </Panel>
                </Group>
              </Panel>

              <ResizablePanelHandle direction="vertical" />

              <Panel
                defaultSize={30}
                minSize={20}
                className="d-flex flex-column h-100"
                style={{ minHeight: 0 }}
              >
                {zonesEditing ? (
                  <div className="card border-0 shadow-lg text-white h-100">
                    <div className="card-body d-flex flex-column p-2" style={{ minHeight: 0 }}>
                      <div className="d-flex justify-content-end mb-2">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setZonesEditing(false)}
                        >
                          Done
                        </Button>
                      </div>
                      <div className="flex-grow-1 d-flex" style={{ minHeight: 0 }}>
                        <ZoneBuilder onPersistSuccess={handleZonePersistSuccess} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <Group
                    orientation="vertical"
                    className="d-flex flex-column h-100"
                    style={{ minHeight: 0 }}
                  >
                    <Panel
                      defaultSize={65}
                      minSize={25}
                      className="d-flex flex-column h-100"
                      style={{ minHeight: 0 }}
                    >
                      <ZoneActivityPanel
                        scheduleIds={zoneScheduleIds}
                        showZoneManager={false}
                        onZonePersistSuccess={handleZonePersistSuccess}
                      />
                    </Panel>
                    <ResizablePanelHandle direction="horizontal" />
                    <Panel
                      defaultSize={35}
                      minSize={20}
                      className="d-flex flex-column h-100"
                      style={{ minHeight: 0 }}
                    >
                      <ZoneTelemetryPanel
                        activeZone={activeZone}
                        scopeZoneIds={zoneScopeIds}
                        linkedSchedulesCount={zoneScheduleIds.length}
                        totalRuns={scopedRuns.length}
                        activeRunsCount={activeRunsCount}
                      />
                    </Panel>
                  </Group>
                )}
              </Panel>
            </Group>
          </Col>
        </Row>
      </Container>
      <ScheduleNudgePrompt
        open={!!scheduleNudge}
        entityType={scheduleNudge?.entityType}
        entityName={scheduleNudge?.entityName}
        onConfirm={handleNudgeConfirm}
        onDismiss={handleNudgeDismiss}
      />
    </div>
  );
};

export function Zones() {
  return (
    <PersonnelProvider>
      <TourProvider>
        <ZonesProvider>
          <ResponderMapProvider>
            <SchedulesProvider>
              <ZonesContent />
            </SchedulesProvider>
          </ResponderMapProvider>
        </ZonesProvider>
      </TourProvider>
    </PersonnelProvider>
  );
}

export default Zones;


